import { useEffect, useRef, useCallback } from "react";
import gsap from "gsap";

// mapping properti internal -> target CSS var yang di-tween GSAP
const PROP_KEYS = {
  position: ["x", "y"],
  rotate: ["rotate"],
  scale: ["scaleX", "scaleY"],
  skew: ["skewX", "skewY"],
};

function valueToGsapVars(prop, value) {
  switch (prop) {
    case "position":
      return { x: value.x, y: value.y };
    case "rotate":
      return { rotate: value };
    case "scale":
      return { scaleX: value.x ?? value, scaleY: value.y ?? value };
    case "skew":
      return { skewX: value.x ?? value, skewY: value.y ?? value };
    default:
      return {};
  }
}

/**
 * Bangun & kontrol gsap.timeline() dari keyframeData.
 * Elemen di-target lewat selector "#<id>" (id svg elemen di canvas).
 */
export function useAnimationEngine(keyframeData, duration) {
  const tlRef = useRef(null);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    if (tlRef.current) {
      tlRef.current.kill();
    }
    const tl = gsap.timeline({ paused: true, repeat: -1 });

    Object.entries(keyframeData).forEach(([elId, props]) => {
      const selector = `#${CSS.escape(elId)}`;
      Object.entries(props).forEach(([propName, kfs]) => {
        if (!kfs || kfs.length < 2) return; // butuh minimal 2 titik buat tween
        const sorted = [...kfs].sort((a, b) => a.time - b.time);

        // set state awal di titik pertama tanpa animasi
        tl.set(selector, valueToGsapVars(propName, sorted[0].value), sorted[0].time);

        for (let i = 1; i < sorted.length; i++) {
          const prev = sorted[i - 1];
          const curr = sorted[i];
          tl.to(
            selector,
            {
              ...valueToGsapVars(propName, curr.value),
              duration: Math.max(curr.time - prev.time, 0.01),
              ease: curr.easing || "power1.inOut",
            },
            prev.time
          );
        }
      });
    });

    tl.duration(duration || tl.duration() || 1);
    tlRef.current = tl;

    return () => tl.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(keyframeData), duration]);

  const seek = useCallback((time) => {
    tlRef.current?.pause().seek(time, false);
  }, []);

  const play = useCallback(() => {
    isPlayingRef.current = true;
    tlRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    isPlayingRef.current = false;
    tlRef.current?.pause();
  }, []);

  const isPlaying = useCallback(() => isPlayingRef.current, []);

  return { seek, play, pause, isPlaying, timeline: tlRef };
}
